import { useCallback, useEffect, useState } from 'react';
import { teamService } from '../../../services/teamService';

const getStorageKey = (userId: number): string => `client-checkout-phone-${userId}`;

/**
 * Remembers the client's last checkout phone in this browser only. The client portal has no
 * encryption key, so the server stores the phone in plaintext only for the life of an active
 * pre-order; the "remembered" copy lives on the client's device instead of in the database.
 */
export function useSavedPhone() {
  const [userId, setUserId] = useState<number | null>(null);
  const [savedPhone, setSavedPhone] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const user = await teamService.getCurrentUser();
        if (cancelled) {
          return;
        }
        setUserId(user.userId);
        setSavedPhone(localStorage.getItem(getStorageKey(user.userId)) ?? '');
      } catch (e) {
        // Not fatal: the client just types the phone
        console.error('Failed to load the saved phone:', e);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const savePhone = useCallback((phone: string) => {
    setSavedPhone(phone);
    if (userId !== null) {
      localStorage.setItem(getStorageKey(userId), phone);
    }
  }, [userId]);

  return {
    data: {
      savedPhone,
    },
    actions: {
      savePhone,
    },
  };
}
